// ============================================================================
// src/firebase.js           ⸺   shared Firebase bootstrap for ALL micro-apps
// Last updated: 20-Jun-2025 (adds Lex Call Angel helpers + Google OAuth popup)
// ============================================================================
//
//   1) ONE canonical “default” Firebase App
//   2) Optionally a named “LexPromptApp” (legacy)
//   3) Typed re-exports for Auth, Firestore, Storage, Functions
//   4) Convenience helpers you can import anywhere in the repo
//
//      import {
//        auth, db, storage, functions,
//        googleSignIn, userDocRef,
//        loadLcaData, saveLcaData,
//      } from '@/firebase';
//
// ============================================================================

import { initializeApp, getApps, getApp }           from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
}                                                   from 'firebase/auth';
import {
  getFirestore,
  connectFirestoreEmulator,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
}                                                   from 'firebase/firestore';
import { getStorage }                               from 'firebase/storage';
import { getFunctions }                             from 'firebase/functions';

// ─────────────────────────────────────────────────────────────────────────────
// 0.  CONFIG  (env vars live in .env.local  /  Netlify / Vercel Secrets etc.)
// ─────────────────────────────────────────────────────────────────────────────
export const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

// ─────────────────────────────────────────────────────────────────────────────
// 1.  Boot or reuse the DEFAULT App  ➜  “lextrack-(default)”
// ─────────────────────────────────────────────────────────────────────────────
const defaultApp = getApps().length
  ? getApp()                       // already initialised somewhere else
  : initializeApp(firebaseConfig); // first (and default) initialisation

// ─────────────────────────────────────────────────────────────────────────────
// 2.  Export core SDK services so every feature file can just “import { db }”
// ─────────────────────────────────────────────────────────────────────────────
export const auth      = getAuth(defaultApp);
export const db        = getFirestore(defaultApp);
export const storage   = getStorage(defaultApp);
export const functions = getFunctions(defaultApp);

// ─────────────────────────────────────────────────────────────────────────────
// 3.  Legacy / multi-tenant: initialise a NAMED App for Lexprompt if missing
// ─────────────────────────────────────────────────────────────────────────────
if (!getApps().some(a => a.name === 'LexPromptApp')) {
  initializeApp(firebaseConfig, 'LexPromptApp');
}

// ─────────────────────────────────────────────────────────────────────────────
// 4.  Auth convenience – Google-popup sign-in & sign-out
// ─────────────────────────────────────────────────────────────────────────────
export const googleProvider = new GoogleAuthProvider();
/** Opens Google OAuth popup → resolves with userCredential */
export const googleSignIn   = () => signInWithPopup(auth, googleProvider);
/** One-liner sign-out */
export const googleSignOut  = () => signOut(auth);

// ─────────────────────────────────────────────────────────────────────────────
// 5.  Handy Firestore helpers  (userDocRef, builderConfig, LexCallAngel data)
// ─────────────────────────────────────────────────────────────────────────────

/** Shortcut to `/users/{uid}` doc ref (defaults to current user) */
export const userDocRef = (uid = auth.currentUser?.uid) =>
  uid ? doc(db, 'users', uid) : null;

/* ---------- generic “builderConfig” used elsewhere ---------- */
export async function getBuilderConfig(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? snap.data().builderConfig || null : null;
}
export async function saveBuilderConfig(uid, builderConfig) {
  return setDoc(
    doc(db, 'users', uid),
    { builderConfig: { ...builderConfig, updatedAt: serverTimestamp() } },
    { merge: true }
  );
}

/* ---------- Lex Call Angel  (LCA)  specialised helpers ---------- */
/** Reads the caller-app blobs (autoCalls / contacts / notes) */
export async function loadLcaData(uid = auth.currentUser?.uid) {
  if (!uid) return null;
  const snap = await getDoc(userDocRef(uid));
  if (!snap.exists()) return null;
  const { lcaData } = snap.data() || {};
  return lcaData || null;   // { autoCalls, contacts, notes }
}

/** Merges the whole LCA payload back to the user doc */
export async function saveLcaData(
  data,                     // { autoCalls, contacts, notes }
  uid = auth.currentUser?.uid
) {
  if (!uid) throw new Error('Not signed-in');
  return setDoc(
    userDocRef(uid),
    { lcaData: { ...data, updatedAt: serverTimestamp() } },
    { merge: true }
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 6.  (Optional)  Local emulator wiring – uncomment while dev-ing offline
// ─────────────────────────────────────────────────────────────────────────────
// if (window.location.hostname === 'localhost') {
//   connectFirestoreEmulator(db, 'localhost', 8081);
//   console.info('📡  Firestore → emulator localhost:8081');
//   // You can ALSO connect Auth/Functions emulators the same way:
//   //   import { connectAuthEmulator } from 'firebase/auth';
//   //   connectAuthEmulator(auth, 'http://localhost:9099');
// }

// ============================================================================
//                      That’s literally all we need 🎉
// ============================================================================
/* ---------- Gmail accounts helpers ---------- */
export async function loadGmailAccounts(uid = auth.currentUser?.uid) {
  if (!uid) return [];
  const snap = await getDoc(userDocRef(uid));
  return snap.exists() ? snap.data().gmailAccounts || [] : [];
}

export async function saveGmailAccounts(accounts, uid = auth.currentUser?.uid) {
  if (!uid) throw new Error('Not signed-in');
  return setDoc(
    userDocRef(uid),
    { gmailAccounts: accounts, updatedAt: serverTimestamp() },
    { merge: true }
  );
}
